<template>
  <div class="flex flex-col gap-4 p-3">
    <div class="flex flex-col gap-2">
      <span class="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
        {{ $t('global.granularity_analysis') }}
      </span>

      <WalkmanButton
        v-model="localGranularity"
        :options="granularityOptions"
        size="small"
        w-full
      />

      <p class="rounded bg-gray-50 px-2.5 py-1.5 text-xs text-gray-600 dark:bg-slate-700 dark:text-gray-300">
        {{ granularityInfo }}
      </p>
    </div>

    <div
      class="flex flex-col gap-2 rounded-md transition-all duration-500"
      :class="isDateRangeHighlighted ? 'bg-primary-50 p-2 ring-2 ring-primary-300 dark:bg-primary-900/20' : ''"
    >
      <span class="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
        {{ $t('global.period_label') }}
      </span>

      <BaseShowEditDateRangePicker
        :start="localDateStart"
        :end="localDateEnd"
        :edition-mode="true"
        @update:start="localDateStart = $event"
        @update:end="localDateEnd = $event"
      />

      <p v-if="rangeSummary" class="text-xs text-gray-500 dark:text-gray-400">
        {{ rangeSummary }}
      </p>
    </div>

    <div class="flex flex-col gap-2">
      <span class="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
        {{ $t('global.date_basis') }}
      </span>

      <div class="flex flex-col gap-1.5">
        <label
          v-for="option in dateBasisOptions"
          :key="option.value"
          class="flex cursor-pointer flex-row items-center gap-2 text-sm"
        >
          <input
            v-model="localBasedOn"
            type="radio"
            :value="option.value"
            class="text-primary-500 focus:ring-primary-400"
          />
          <span>{{ option.label }}</span>
        </label>
      </div>
    </div>

    <div class="flex flex-col gap-2">
      <span class="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
        {{ $t('attributes.funeral_home') }}
      </span>
      <SelectComponent
        v-model="localFuneralHomes"
        :values="funeralHomeOptions"
        :multiple="true"
        track-by="id"
        display-key="name"
      />
    </div>

    <div class="flex flex-col gap-2">
      <span class="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
        {{ $t('attributes.company') }}
      </span>
      <SelectComponent
        v-model="localCompanies"
        :values="companyOptions"
        :multiple="true"
        track-by="id"
        display-key="name"
      />
    </div>

    <div class="flex flex-col gap-2">
      <span class="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
        {{ $t('attributes.responsable') }}
      </span>
      <SelectComponent
        v-model="localResponsables"
        name="analytics_responsable_filter"
        :values="responsableOptions"
        :multiple="true"
        track-by="id"
        display-key="name"
      />
    </div>

    <div class="flex flex-col gap-2">
      <span class="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
        {{ $t('global.comparative_period') }}
      </span>
      <div class="flex flex-col gap-1.5">
        <label class="flex cursor-pointer flex-row items-center gap-2 text-sm">
          <input
            v-model="localCompareMode"
            type="radio"
            value="full"
            class="text-primary-500 focus:ring-primary-400"
          />
          <span>{{ $t('global.compare_mode_full') }}</span>
        </label>
        <label class="flex cursor-pointer flex-row items-center gap-2 text-sm">
          <input
            v-model="localCompareMode"
            type="radio"
            value="today"
            class="text-primary-500 focus:ring-primary-400"
          />
          <span>{{ $t('global.compare_mode_today') }}</span>
        </label>
      </div>
    </div>

    <div v-if="isInvoicingDashboard" class="flex flex-col gap-2">
      <span class="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
        {{ $t('attributes.doc_type') }}
      </span>
      <SelectComponent
        v-model="localDocTypes"
        name="analytics_doc_type_filter"
        :values="docTypeOptions"
        :multiple="true"
        :none-selected-label="docTypeNoneSelectedLabel"
        track-by="id"
        display-key="name"
      />
      <p v-if="localDocTypes.length === 0" class="text-xs italic text-gray-400 dark:text-gray-500">
        {{ $t('global.doc_type_default_hint') }}
      </p>
    </div>

    <button
      type="button"
      class="bg-primary-500 hover:bg-primary-600 w-full rounded py-2 text-sm font-semibold text-white transition-colors"
      @click="apply"
    >
      {{ $t('global.filter') }}
    </button>
  </div>
</template>

<script>
import moment from 'moment'
import { mapGetters } from 'vuex'
import WalkmanButton from '@c/WalkmanButton.vue'
import BaseShowEditDateRangePicker from '@c/addf-package/components/BaseShowEditDatePicker/BaseShowEditDateRangePicker.vue'
import SelectComponent from '@c/BaseFormComponent/SelectComponent.vue'

export default {
  name: 'AnalyticsTimeFilterPanel',

  components: { WalkmanButton, BaseShowEditDateRangePicker, SelectComponent },

  props: {
    granularity: {
      type: String,
      default: 'month'
    },
    highlightDateRangeTrigger: {
      type: Number,
      default: 0
    },
    basedOn: {
      type: String,
      default: 'element_date'
    },
    dateStart: {
      type: String,
      default: null
    },
    dateEnd: {
      type: String,
      default: null
    },
    funeralHomes: {
      type: Array,
      default: () => []
    },
    companies: {
      type: Array,
      default: () => []
    },
    compareMode: {
      type: String,
      default: 'full'
    },
    /** Currently active responsable filter values (user ids). */
    responsables: {
      type: Array,
      default: () => []
    },
    /** Whether the current dashboard is an invoicing analytics dashboard. */
    isInvoicingDashboard: {
      type: Boolean,
      default: false
    },
    /**
     * Currently active doc type filter values (e.g. ['INV', 'QUO']).
     * Empty array means no filter is active (backend default INV applies).
     */
    docTypes: {
      type: Array,
      default: () => []
    }
  },

  emits: ['apply'],

  data() {
    return {
      localGranularity: this.granularity,
      localBasedOn: this.basedOn,
      localDateStart: this.dateStart ?? moment().startOf('month').format('YYYY-MM-DD'),
      localDateEnd: this.dateEnd ?? moment().endOf('month').format('YYYY-MM-DD'),
      isDateRangeHighlighted: false,
      localFuneralHomes: [...this.funeralHomes],
      localCompanies: [...this.companies],
      localCompareMode: this.compareMode,
      localResponsables: [...this.responsables],
      localDocTypes: [...this.docTypes]
    }
  },

  computed: {
    granularityOptions() {
      return [
        { value: 'day', label: this.$t('global.day') },
        { value: 'week', label: this.$t('global.week') },
        { value: 'month', label: this.$t('global.month') },
        { value: 'quarter', label: this.$t('global.quarter') },
        { value: 'year', label: this.$t('global.year') }
      ]
    },

    dateBasisOptions() {
      return [
        { value: 'element_date', label: this.$t('global.date_basis_element') },
        { value: 'created_at', label: this.$t('global.date_basis_created_at') },
        { value: 'ceremony_date', label: this.$t('global.date_basis_ceremony') },
        { value: 'death_date', label: this.$t('global.date_basis_death') }
      ]
    },

    granularityInfo() {
      const key = `global.granularity_info_${this.localGranularity}`
      return this.$t(key)
    },

    rangeSummary() {
      if (!this.localDateStart || !this.localDateEnd) {
        return null
      }

      const compareStart = moment(this.localDateStart).subtract(1, 'year')
      const compareEnd =
        this.localCompareMode === 'today'
          ? moment.min(moment(), moment(this.localDateEnd)).subtract(1, 'year')
          : moment(this.localDateEnd).subtract(1, 'year')
      const compareLabel = `${compareStart.format('D MMM YYYY')} → ${compareEnd.format('D MMM YYYY')}`

      return `${this.$t('global.comparative_period')} : ${compareLabel}`
    },

    /**
     * Available doc type options for the multiselect, filtered to the 5 invoicing
     * doc types (INV, QUO, PURO, CN, INV_PRO). Uses the metadata enum so labels
     * are translated server-side (same source as the invoice wizard doc type step).
     * @returns {{ id: string, name: string }[]}
     */
    docTypeOptions() {
      return this.invoicingDocTypes.filter((item) =>
        ['INV', 'QUO', 'PURO', 'CN', 'INV_PRO'].includes(item.id)
      )
    },

    /**
     * Label shown when no doc type is selected: the backend then defaults to
     * invoices only, so the effective value « Factures » is displayed.
     * Capitalized manually: MultiSelectComponent (used here since this select
     * is multiple + non-searchable) has no `capitalize`/capitalize-first
     * support, unlike SelectSimple2Component/MultiSelectSearchableComponent.
     * @returns {string}
     */
    docTypeNoneSelectedLabel() {
      const label = this.$t('global.invoices')
      return label.charAt(0).toUpperCase() + label.slice(1)
    },

    /**
     * Available responsables for the multiselect, from the users metadata.
     * @returns {{ id: string, name: string }[]}
     */
    responsableOptions() {
      return (this.users ?? []).map((user) => ({ id: user.id, name: user.full_name }))
    },

    ...mapGetters({
      funeralHomeOptions: 'metadata/getFuneralHomesMetadata',
      companyOptions: 'metadata/getCompaniesMetadata',
      invoicingDocTypes: 'metadata/getInvoiceDocTypes',
      users: 'metadata/getUsersMetadata'
    })
  },

  watch: {
    highlightDateRangeTrigger(value) {
      if (value > 0) {
        this.isDateRangeHighlighted = true
        setTimeout(() => {
          this.isDateRangeHighlighted = false
        }, 2000)
      }
    },
    granularity(value) {
      this.localGranularity = value
    },
    basedOn(value) {
      this.localBasedOn = value
    },
    dateStart(value) {
      this.localDateStart = value ?? moment().startOf('month').format('YYYY-MM-DD')
    },
    dateEnd(value) {
      this.localDateEnd = value ?? moment().endOf('month').format('YYYY-MM-DD')
    },
    funeralHomes(value) {
      this.localFuneralHomes = [...value]
    },
    companies(value) {
      this.localCompanies = [...value]
    },
    compareMode(value) {
      this.localCompareMode = value
    },
    responsables(value) {
      this.localResponsables = [...value]
    },
    docTypes(value) {
      this.localDocTypes = [...value]
    }
  },

  methods: {
    apply() {
      this.$emit('apply', {
        granularity: this.localGranularity,
        basedOn: this.localBasedOn,
        dateStart: this.localDateStart,
        dateEnd: this.localDateEnd,
        funeralHomes: this.localFuneralHomes,
        companies: this.localCompanies,
        compareMode: this.localCompareMode,
        responsables: this.localResponsables,
        docTypes: this.localDocTypes
      })
    }
  }
}
</script>
